import { useMemo, useState } from "react";
import { Dialog } from "@hybrid-iptv/ui";
import { filterTopics, type HelpTopic } from "../settings/helpTopics.js";

/**
 * Settings → Help (feature-plan §11, launch guide G9): every screen and button the app has, in
 * one place, in the words a viewer would use.
 *
 * The list is the topic list; picking one opens it over the page, so the answers stay two presses
 * from anywhere on the TV and Back always returns to the list. The words live in
 * `settings/helpTopics.ts`, and the remote's own section is generated from the same map the app
 * dispatches through (`remote/remoteMap.ts`), so it cannot describe a different remote.
 *
 * No screenshots, deliberately: they would be pictures of a 10-foot UI, unreadable at the size the
 * panel can show, unreadable again on the next box, stale after any redesign, and megabytes in the
 * download. Descriptions use the glyphs the bar actually shows (⚙ ⏪ ⏩ ⏺ ⇄ ▤ ⓘ ⧉ ⊞ ✕ ↻), which is
 * what a viewer looks for on the screen.
 */
export function HelpPage() {
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);
  const topics = useMemo(() => filterTopics(query), [query]);
  const open = topics.find((topic) => topic.id === openId) ?? null;

  return (
    <div className="page page--help">
      <div className="settings-card">
        <div className="settings-header">
          <h1 className="settings-title">Help</h1>
          <p className="settings-subtitle">
            Every screen and button in the app, in plain words. Pick a topic; Back or Close comes
            straight back here.
          </p>
        </div>

        <div className="help-search">
          <input
            className="help-search__input"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search help…"
            aria-label="Search help"
          />
          {query !== "" && (
            <button type="button" className="ui-btn ui-btn--ghost" onClick={() => setQuery("")} aria-label="Clear search">
              Clear
            </button>
          )}
        </div>

        {topics.length === 0 ? (
          <p className="settings-row-hint">
            Nothing matches “{query}”. Try one word — remote, recording, subtitles, multiview.
          </p>
        ) : (
          <div className="help-topics">
            {topics.map((topic) => (
              <button
                key={topic.id}
                type="button"
                className="help-topic"
                onClick={() => setOpenId(topic.id)}
              >
                <svg className="help-topic__icon" viewBox="0 0 24 24" aria-hidden>
                  <path d={topic.icon} />
                </svg>
                <span className="help-topic__body">
                  <span className="help-topic__title">{topic.title}</span>
                  <span className="help-topic__summary">{topic.summary}</span>
                </span>
                <svg className="help-topic__chevron" viewBox="0 0 24 24" aria-hidden>
                  <path d="m9 5 7 7-7 7" />
                </svg>
              </button>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open !== null} title={open?.title ?? "Help"} onClose={() => setOpenId(null)}>
        {open && <HelpTopicBody topic={open} />}
      </Dialog>
    </div>
  );
}

/**
 * One bullet: a line that names a button or a setting before an em dash gets the name picked out,
 * so the remote's table and the player's bar can be skimmed for the button in your hand.
 */
function Bullet({ text }: { text: string }) {
  const split = text.indexOf(" — ");
  const name = split > 0 ? text.slice(0, split) : "";
  // Only a short name — the rest of the sentence stays as written.
  if (name !== "" && name.length <= 32 && !name.includes(".")) {
    return (
      <li>
        <strong>{name}</strong>
        {text.slice(split)}
      </li>
    );
  }
  return <li>{text}</li>;
}

function HelpTopicBody({ topic }: { topic: HelpTopic }) {
  return (
    <div className="help-body">
      {topic.sections.map((section, index) => (
        <section className="help-section" key={`${topic.id}-${index}`}>
          {section.heading && <h3 className="help-section__heading">{section.heading}</h3>}
          {section.paragraphs?.map((text, at) => (
            <p className="help-section__p" key={at}>
              {text}
            </p>
          ))}
          {section.bullets && (
            <ul className="help-section__list">
              {section.bullets.map((text, at) => (
                <Bullet key={at} text={text} />
              ))}
            </ul>
          )}
        </section>
      ))}
    </div>
  );
}
